import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWizard } from "../../context/WizardContext";
import { bookLanguageDisplay } from "../../i18n/bookLanguageDisplay";
import { useTranslation } from "../../i18n/useTranslation";
import Modal from "./Modal";
import { useAuth } from "../../context/AuthContext";
import LogoLoader from "../common/LogoLoader";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";

const StoryStudio: React.FC = () => {
  const { t } = useTranslation();
  const { 
    sessionId, 
    language, 
    storyType,
    storyStyle,
    images, 
    storyDescription,
    generateStory,
    isLoading,
    error,
    clearError,
    resetWizard
  } = useWizard();

  const { user, getAuthHeader } = useAuth();
  
  const navigate = useNavigate();
  const frontendDevMode = process.env.REACT_APP_PRODUCTION_MODE !== 'true';
  const [generatedStory, setGeneratedStory] = useState<string | null>(null);
  const [apiUsage, setApiUsage] = useState<any>(null);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating-story' | 'completed' | 'failed'>('idle');
  const [usedFallback, setUsedFallback] = useState<boolean>(false);
  const [developmentPrompt, setDevelopmentPrompt] = useState<string | null>(null);
  const [systemMessage, setSystemMessage] = useState<string | null>(null);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState<boolean>(frontendDevMode);
  const [localError, setLocalError] = useState<string | null>(null);
  const [storyPages, setStoryPages] = useState<string[]>([]);
  const [editingPageIndex, setEditingPageIndex] = useState<number | null>(null);
  const [editingPageContent, setEditingPageContent] = useState<string>('');
  const [isStoryDetailsExpanded, setIsStoryDetailsExpanded] = useState<boolean>(false);
  const [isImagesExpanded, setIsImagesExpanded] = useState<boolean>(false);
  const [regeneratingPageIndex, setRegeneratingPageIndex] = useState<number | null>(null);
  const [regenerationsUsed, setRegenerationsUsed] = useState<number>(0);
  const [regenerationsLimit, setRegenerationsLimit] = useState<number>(3);
  const [regenerationsRemaining, setRegenerationsRemaining] = useState<number>(3);
  const [pastedStoryText, setPastedStoryText] = useState<string>('');
  const [isCompletePromptExpanded, setIsCompletePromptExpanded] = useState<boolean>(false);
  const [isPromptCopied, setIsPromptCopied] = useState<boolean>(false);
  const latestStoryRef = useRef<string>('');
  
  // Modal states
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    icon?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  useEffect(() => {
    if (user?.isAdmin === true && sessionId) {
      fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(sessionId)}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ adminLastStep: 'studio' }),
      }).catch(() => {});
    }

    // Check if we have all required data
    if (!sessionId || !storyDescription || images.length === 0) {
      navigate("/create/language");
      return;
    }

    // Re-hydrate from backend so returning to this page shows last saved admin paste.
    const loadExisting = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/stories/${encodeURIComponent(sessionId)}?t=${Date.now()}`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return false;
        const story = data?.data;
        if (story && typeof story.generatedStory === 'string' && story.generatedStory.trim().length > 0) {
          setIsDevelopmentMode(true);
          // If admin previously pasted story text, show it again in the paste box and pages.
          setGeneratedStory(story.generatedStory);
          latestStoryRef.current = story.generatedStory.trim();
          parseStoryIntoPages(story.generatedStory);
          setGenerationStatus('completed');
          return true;
        }
      } catch {
        // ignore
      }
      return false;
    };

    // Always load the prompt UI; but don't overwrite existing story if found.
    if (generationStatus === 'idle') {
      loadExisting().then((found) => {
        handleGenerateStory({ skipMockStory: found });
      });
    }
  }, [user, sessionId, storyDescription, images, navigate, getAuthHeader]);

  // Track the latest story text so navigation/unmount always saves the newest value.
  useEffect(() => {
    latestStoryRef.current = (pastedStoryText || generatedStory || '').trim();
  }, [pastedStoryText, generatedStory]);

  const handleGenerateStory = async (opts?: { skipMockStory?: boolean }) => {
    setGenerationStatus('generating-story');
    clearError();
    setLocalError(null);

    try {
      const result = await generateStory();
      
      if (result && result.generatedStory) {
        // Production mode - story was generated by AI
        setIsDevelopmentMode(false);
        setGeneratedStory(result.generatedStory);
        setApiUsage(result.usage);
        setUsedFallback(result.fallback || false);
        setGenerationStatus('completed');
        
        // Parse story into individual pages
        parseStoryIntoPages(result.generatedStory);
        
        // Load regeneration status
        loadRegenerationStatus();
      } else if (result && result.prompt) {
        // Development mode - prompt returned without API call
        setIsDevelopmentMode(true);
        setDevelopmentPrompt(result.prompt);
        setSystemMessage(result.systemMessage || null);
        setGenerationStatus('completed');
        
        // Create a mock story only when we don't already have a saved story (admin resumed session).
        if (!opts?.skipMockStory) {
          const mockStory = createMockStory(language, storyDescription, images);
          setGeneratedStory(mockStory);
          parseStoryIntoPages(mockStory);
        }
      } else {
        // Error case
        setGenerationStatus('failed');
        setLocalError('Failed to generate story. Please try again.');
      }
    } catch (err: any) {
      console.error('Story generation failed:', err);
      setGenerationStatus('failed');
      setLocalError(err.message || 'Failed to generate story. Please try again.');
    }
  };

  // Create a mock story for development mode
  const createMockStory = (lang: string | null, description: string, imgs: any[]): string => {
    const isHebrew = lang === 'Hebrew';
    // Format image tags as "Name: X, Role: Y" for display
    const imageTags = imgs
      .map(img => img.tags)
      .filter(tag => tag)
      .join(', ') || 'family members';
    
    if (isHebrew) {
      return `עמוד 1:
זהו סיפור על ${description.substring(0, 50)}...
של משפחה אוהבת ומאושרת,
עם הרבה אהבה וחלומות גדולים
להקים ביחד משפחה עם ילדים.

עמוד 2:
במשך מספר שנים נפלאות,
יצרו המשפחה שלל של חוויות.
הרבה צחוק, אהבה ושמחה
במטרה להקים ביחד משפחה.

עמוד 3:
${imageTags} היו חלק מהסיפור,
עם הרבה רגעים של כיף וצחוק.
יחד הם יצרו זיכרונות יפים
שיישארו איתם לכל החיים.

עמוד 4:
כל יום היה מלא בהרפתקאות,
עם משחקים, צחוקים וחוויות.
המשפחה גדלה והתחזקה
עם אהבה שתמיד תישאר.

עמוד 5:
הסיפור שלנו ממשיך להתפתח,
עם רגעים מיוחדים וזיכרונות.
כל יום מביא משהו חדש
למשפחה שלנו האהובה.

עמוד 6:
עם ${imageTags} ביחד,
אנחנו יוצרים סיפור נפלא.
כל רגע הוא מתנה מיוחדת
במשפחה שלנו המאושרת.`;
    } else {
      return `Page 1:
This is a story about ${description.substring(0, 50)}...
A family full of love and joy,
With happy moments every day,
Creating memories that never fade away.

Page 2:
Through many wonderful years,
The family shared laughter and cheers.
With lots of fun and happy times,
Building memories that always shine.

Page 3:
${imageTags} were part of the tale,
With adventures that never fail.
Together they made special days,
In so many different ways.

Page 4:
Each day was filled with fun and play,
With games and laughter all the way.
The family grew and stayed so strong,
With love that lasts our whole life long.

Page 5:
Our story keeps on growing bright,
With special moments day and night.
Each day brings something new to see,
In our happy family.

Page 6:
With ${imageTags} together,
We create a story that will last forever.
Every moment is a special gift,
In our family that's so swift.`;
    }
  };

  const parseStoryIntoPages = (story: string) => {
    if (!story || story.trim().length === 0) {
      console.error('❌ No story to parse');
      return;
    }

    console.log('🔍 Parsing story:', story.substring(0, 200) + '...');
    
    const isHebrew = language === 'Hebrew';
    let pages: string[] = [];
    
    try {
      if (isHebrew) {
        // Split by Hebrew page markers - more flexible pattern
        const pagePattern = /עמוד\s+\d+\s*:/g;
        const parts = story.split(pagePattern);
        pages = parts.slice(1).map(page => page.trim()).filter(page => page.length > 0);
        
        // If no Hebrew markers found, try alternative parsing
        if (pages.length === 0) {
          // Try splitting by double line breaks as fallback
          const fallbackPages = story.split(/\n\n+/).filter(page => page.trim().length > 0);
          pages = fallbackPages.length > 1 ? fallbackPages : [story.trim()];
        }
      } else {
        // Split by English page markers - more flexible pattern
        const pagePattern = /Page\s+\d+\s*:/gi;
        const parts = story.split(pagePattern);
        pages = parts.slice(1).map(page => page.trim()).filter(page => page.length > 0);
        
        // If no English markers found, try alternative parsing
        if (pages.length === 0) {
          // Try splitting by double line breaks as fallback
          const fallbackPages = story.split(/\n\n+/).filter(page => page.trim().length > 0);
          pages = fallbackPages.length > 1 ? fallbackPages : [story.trim()];
        }
      }
      
      // Ensure we have at least one page
      if (pages.length === 0) {
        console.warn('⚠️ No pages found, using entire story as single page');
        pages = [story.trim()];
      }
      
      setStoryPages(pages);
      console.log(`✅ Successfully parsed ${pages.length} pages`);
      pages.forEach((page, index) => {
        console.log(`Page ${index + 1} preview:`, page.substring(0, 50) + '...');
      });
      
    } catch (error) {
      console.error('❌ Error parsing story:', error);
      // Fallback: treat entire story as one page
      setStoryPages([story.trim()]);
    }
  };

  const handleEditPage = (pageIndex: number) => {
    setEditingPageIndex(pageIndex);
    setEditingPageContent(storyPages[pageIndex]);
  };

  const handleSavePage = async () => {
    if (editingPageIndex !== null) {
      const updatedPages = [...storyPages];
      updatedPages[editingPageIndex] = editingPageContent.trim();
      setStoryPages(updatedPages);
      
      // Reconstruct the full story
      const isHebrew = language === 'Hebrew';
      const reconstructedStory = updatedPages.map((page, index) => {
        const pageMarker = isHebrew ? `עמוד ${index + 1}:` : `Page ${index + 1}:`;
        return `${pageMarker}\n${page}`;
      }).join('\n\n');
      
      setGeneratedStory(reconstructedStory);
      
      // Save to backend database
      try {
        const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/story`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
          body: JSON.stringify({ 
            generatedStory: reconstructedStory 
          }),
        });

        const data = await response.json();
        
        if (!response.ok) {
          console.error('Failed to save story to backend:', data.message);
          setLocalError(data?.message || 'Failed to save story to backend');
          // Still continue with local changes even if backend save fails
        } else {
          console.log('✅ Story saved to backend successfully');
        }
      } catch (error) {
        console.error('❌ Error saving story to backend:', error);
        setLocalError('Failed to save story to backend');
        // Still continue with local changes even if backend save fails
      }
      
      setEditingPageIndex(null);
      setEditingPageContent('');
      
      console.log(`✅ Saved changes to page ${editingPageIndex + 1}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingPageIndex(null);
    setEditingPageContent('');
  };

  const saveStoryToDatabase = async (storyToSave: string) => {
    if (!sessionId || !storyToSave) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/story`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ 
          generatedStory: storyToSave 
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || 'Failed to save story to backend');
      console.log('✅ Story saved to backend successfully');
    } catch (error) {
      console.error('❌ Error saving story to backend:', error);
      throw error;
    }
  };

  // Admin flow: best-effort save on unmount so leaving to Home won't lose work.
  useEffect(() => {
    return () => {
      if (user?.isAdmin !== true) return;
      const text = latestStoryRef.current;
      if (!text) return;
      // Fire-and-forget; don't block navigation.
      saveStoryToDatabase(text);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isAdmin, sessionId]);

  const showModal = (
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm',
    title: string,
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
    icon?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      icon
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    closeModal();
  };

  const loadRegenerationStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/regenerations`, {
        headers: { ...getAuthHeader() },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegenerationsUsed(data.data.regenerationsUsed);
        setRegenerationsLimit(data.data.regenerationsLimit);
        setRegenerationsRemaining(data.data.regenerationsRemaining);
      }
    } catch (error) {
      console.error('Failed to load regeneration status:', error);
    }
  };

  const handleRegeneratePage = async (pageIndex: number) => {
    // Check if user has regenerations left
    if (regenerationsRemaining <= 0) {
      showModal(
        'error',
        'No Regenerations Left',
        `You've used all ${regenerationsLimit} regenerations for this story! Each story comes with ${regenerationsLimit} regenerations to help you perfect your pages.`,
        undefined,
        undefined,
        '🚫'
      );
      return;
    }

    // Show confirmation with remaining count
    const confirmRegeneration = () => {
      performRegeneration(pageIndex);
    };

    showModal(
      'confirm',
      'Regenerate Page',
      `You have ${regenerationsRemaining}/${regenerationsLimit} regenerations remaining. This will use ChatGPT to rewrite page ${pageIndex + 1} with fresh content while keeping the same theme.`,
      confirmRegeneration,
      'Regenerate Page',
      '🔄'
    );
  };

  const performRegeneration = async (pageIndex: number) => {
    setRegeneratingPageIndex(pageIndex);
    clearError();

    try {
      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/regenerate-page`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          pageIndex: pageIndex,
          currentPageContent: storyPages[pageIndex],
          language: language,
          storyDescription: storyDescription,
          imageTags: images.map(img => img.tags).filter(tag => tag)
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 429) {
          // Regeneration limit reached
          showModal(
            'error',
            'Regeneration Limit Reached',
            `You've used all ${regenerationsLimit} regenerations for this story! Each story comes with ${regenerationsLimit} regenerations to help you perfect your pages.`,
            undefined,
            'Got it',
            '🚫'
          );
          return;
        }
        throw new Error(data.message || 'Failed to regenerate page');
      }

      // Update the specific page with new content
      const updatedPages = [...storyPages];
      updatedPages[pageIndex] = data.data.newPageContent;
      setStoryPages(updatedPages);
      
      // Update regeneration counts
      setRegenerationsUsed(data.data.regenerationsUsed);
      setRegenerationsRemaining(data.data.regenerationsRemaining);
      
      // Reconstruct the full story
      const isHebrew = language === 'Hebrew';
      const reconstructedStory = updatedPages.map((page, index) => {
        const pageMarker = isHebrew ? `עמוד ${index + 1}:` : `Page ${index + 1}:`;
        return `${pageMarker}\n${page}`;
      }).join('\n\n');
      
      setGeneratedStory(reconstructedStory);
      
      console.log(`✅ Successfully regenerated page ${pageIndex + 1}. ${data.data.regenerationsRemaining} regenerations remaining.`);
      
      // Show success message with remaining count
      showModal(
        'success',
        'Page Regenerated!',
        `Page ${pageIndex + 1} has been successfully rewritten! You have ${data.data.regenerationsRemaining} regenerations remaining for this story.`,
        undefined,
        'Great!',
        '✨'
      );
      
    } catch (err: any) {
      console.error('Failed to regenerate page:', err);
      showModal(
        'error',
        'Regeneration Failed',
        `Sorry, we couldn't regenerate the page right now. ${err.message}`,
        undefined,
        'Try Again Later',
        '❌'
      );
    } finally {
      setRegeneratingPageIndex(null);
    }
  };

  const handleBack = () => {
    navigate("/create/story-details");
  };

  const handleStartOver = () => {
    resetWizard();
    navigate("/");
  };

  const downloadStory = () => {
    if (!generatedStory) {
      showModal(
        'warning',
        'No Story to Download',
        'Please wait for your story to be generated before downloading.',
        undefined,
        'OK',
        '📄'
      );
      return;
    }

    const storyContent = `My AI-Generated Family Story
${t("wizard.story.summaryLanguage")} ${bookLanguageDisplay(language, t)}
Created: ${new Date().toLocaleDateString()}
Generation Mode: Production (AI Generated)

Story Description:
${storyDescription}

Generated Story:
${generatedStory}

---
Images used: ${images.length}
${images.map((img, index) => `${index + 1}. ${img.tags || 'No tags'}`).join('\n')}

${apiUsage ? `
API Usage:
- ChatGPT Tokens: ${apiUsage.totalTokens || 'N/A'}
` : ''}
`;

    const blob = new Blob([storyContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-family-story-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Show success modal
    showModal(
      'success',
      'Story Downloaded!',
      'Your story has been successfully downloaded to your device.',
      undefined,
      'Great!',
      '💾'
    );
  };

  const getStatusMessage = () => {
    switch (generationStatus) {
      case 'generating-story':
        return '🤖 AI is writing your personalized story...';
      case 'completed':
        return '✨ Your story is ready!';
      case 'failed':
        return '❌ Something went wrong. Please try again.';
      default:
        return '🔄 Preparing your story...';
    }
  };

  const getProgressPercentage = () => {
    switch (generationStatus) {
      case 'generating-story':
        return 50;
      case 'completed':
        return 100;
      case 'failed':
        return 0;
      default:
        return 10;
    }
  };

  const storyTypeLabel =
    storyType === 'surrogacy'
      ? t('wizard.storyType.surrogacyName')
      : storyType === 'general'
        ? t('wizard.storyType.generalName')
        : '—';
  const storyStyleLabel =
    storyStyle
      ? t(`wizard.storyStyle.${storyStyle}Title` as any)
      : '—';

  return (
    <div 
      className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
      style={{
        backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <main
        id="main-content"
        tabIndex={-1}
        className="relative flex w-full max-w-4xl flex-1 flex-col items-center outline-none"
      >
      {/* Error Display */}
      {(error || localError) && (
        <div
          role="alert"
          aria-live="polite"
          className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-card shadow-soft mb-4 max-w-4xl w-full"
        >
          <span className="block sm:inline">{error || localError}</span>
          <button
            type="button"
            onClick={() => {
              clearError();
              setLocalError(null);
            }}
            aria-label="Dismiss error"
            className="float-end ms-2 text-red-700 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-card border border-white/40 max-w-4xl w-full z-10">
        <h1 className="text-3xl font-extrabold text-heading mb-3 text-center">
          Story Studio 🎬
        </h1>
        <AdminPageNavigator align="center" />

        {/* Story Summary - Collapsible */}
        <div className="bg-gray-50 rounded-lg mb-6 overflow-hidden">
          <button
            onClick={() => setIsStoryDetailsExpanded(!isStoryDetailsExpanded)}
            className="w-full p-6 text-left hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">📖 Your Story Details</h2>
              <span className="text-gray-500 text-lg">
                {isStoryDetailsExpanded ? '▼' : '▶'}
              </span>
            </div>
          </button>
          
          {isStoryDetailsExpanded && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <p>
                    <strong>{t("wizard.story.summaryLanguage")}</strong>{" "}
                    {bookLanguageDisplay(language, t)}
                  </p>
                  <p><strong>{t("wizard.story.summaryStoryType")}</strong> {storyTypeLabel}</p>
                  <p><strong>{t("wizard.details.sumStyle")}:</strong> {storyStyleLabel}</p>
                  <p><strong>Images:</strong> {images.length} uploaded</p>
                </div>
                <div>
                  <p><strong>Description:</strong></p>
                  <p className="text-sm text-gray-600 mt-1 p-2 bg-white rounded">
                    {storyDescription.length > 150 
                      ? `${storyDescription.substring(0, 150)}...` 
                      : storyDescription
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Uploaded Images Preview - Collapsible */}
        <div className="bg-gray-50 rounded-lg mb-6 overflow-hidden">
          <button
            onClick={() => setIsImagesExpanded(!isImagesExpanded)}
            className="w-full p-6 text-left hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">📸 Your Original Photos</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {images.length} image{images.length !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-500 text-lg">
                  {isImagesExpanded ? '▼' : '▶'}
                </span>
              </div>
            </div>
          </button>
          
          {isImagesExpanded && (
            <div className="px-6 pb-6 border-t border-gray-200">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                {images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image.preview}
                      alt={image.accessibleDescription?.trim() || ""}
                      className="w-full h-24 object-contain rounded-md bg-white border shadow-sm"
                    />
                    {image.tags && (() => {
                      const nameMatch = image.tags.match(/name:\s*([^,]+)/i);
                      const roleMatch = image.tags.match(/role:\s*([^,]+)/i);
                      const name = nameMatch ? nameMatch[1].trim() : '';
                      const role = roleMatch ? roleMatch[1].trim() : '';
                      const displayText = name && role ? `${name} (${role})` : (name || role || image.tags);
                      return (
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 rounded-b-md truncate">
                          {displayText}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Loading Animation */}
        {(isLoading || generationStatus === 'generating-story') && (
          <div className="mb-6 p-6 bg-blue-50 rounded-lg text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-blue-800 font-medium">
              ChatGPT is crafting your personalized story...
            </p>
            <p className="text-blue-600 text-sm mt-2">
              This may take 30-60 seconds. Please don't close this page.
            </p>
          </div>
        )}

        {/* Development Mode - Show Combined Prompt */}
        {isDevelopmentMode && (
          <div className="bg-blue-50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-blue-800 mb-4">📝 OpenAI Prompt</h2>
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setIsCompletePromptExpanded((prev) => !prev)}
                className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                aria-expanded={isCompletePromptExpanded}
                aria-controls="complete-prompt-panel"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">Complete Prompt</h3>
                  <span className="text-gray-500 text-lg">
                    {isCompletePromptExpanded ? '▼' : '▶'}
                  </span>
                </div>
              </button>

              {isCompletePromptExpanded && (
                <div id="complete-prompt-panel" className="bg-white rounded-lg p-4 border border-gray-200 mt-2">
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">SYSTEM MESSAGE:</p>
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-2 rounded">{systemMessage || 'N/A'}</pre>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">USER PROMPT:</p>
                    <pre className="whitespace-pre-wrap text-sm text-gray-800">{developmentPrompt || 'Loading prompt…'}</pre>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const fullPrompt = `SYSTEM MESSAGE:\n${systemMessage || 'N/A'}\n\nUSER PROMPT:\n${developmentPrompt || ''}`;
                  try {
                    await navigator.clipboard.writeText(fullPrompt);
                    setIsPromptCopied(true);
                    window.setTimeout(() => setIsPromptCopied(false), 2000);
                  } catch {
                    setLocalError("Failed to copy prompt to clipboard.");
                  }
                }}
                disabled={!developmentPrompt}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {isPromptCopied ? "✅ Copied" : "📋 Copy Complete Prompt"}
              </button>
            </div>

            {/* Paste AI Story Textbox - Development Mode Only */}
            <div className="mt-6">
              <label htmlFor="story-paste-dev" className="text-lg font-semibold text-blue-800 mb-2 block">
                📝 Paste AI-Generated Story Here
              </label>
              <p id="story-paste-dev-hint" className="text-sm text-gray-600 mb-3">
                After copying the prompt and getting the story from ChatGPT, paste it here. It will automatically split into pages below.
              </p>
              <textarea
                id="story-paste-dev"
                aria-describedby="story-paste-dev-hint"
                value={pastedStoryText}
                onChange={(e) => {
                  const text = e.target.value;
                  latestStoryRef.current = text.trim();
                  setPastedStoryText(text);
                  
                  // Automatically parse and split into pages when text is pasted
                  if (text.trim().length > 0) {
                    // Update the generated story
                    setGeneratedStory(text);
                    latestStoryRef.current = text.trim();
                    
                    // Parse into pages
                    parseStoryIntoPages(text);
                    
                    // Save to database asynchronously
                    if (sessionId) {
                      saveStoryToDatabase(text);
                    }
                  } else {
                    // Clear story if textbox is cleared
                    setGeneratedStory(null);
                    setStoryPages([]);
                    latestStoryRef.current = '';
                  }
                }}
                placeholder="Paste the complete story from ChatGPT here... It will automatically split into pages below."
                className="w-full h-48 p-4 border-2 border-blue-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              />
              {pastedStoryText.trim().length > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ Story pasted! {storyPages.length} page{storyPages.length !== 1 ? 's' : ''} detected below.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Generated Story Display with Page-by-Page Editing (Production Mode Only) */}
        {generatedStory && !isDevelopmentMode && (
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-green-800">✨ Your AI-Generated Story</h2>
              <div className="flex items-center gap-2">
                {usedFallback && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                    📚 Fallback Story
                  </span>
                )}
                <span className="text-sm text-gray-600">
                  {storyPages.length} page{storyPages.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            
            {usedFallback && (
              <div className="bg-yellow-50 p-3 rounded-lg mb-4 border-l-4 border-yellow-400">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> We used a sample story because ChatGPT was temporarily unavailable. 
                  You can still edit each page to personalize it!
                </p>
              </div>
            )}
            
            {/* Story Pages - Editable */}
            {storyPages.length > 0 ? (
              <div className="space-y-4 mb-4">
                {storyPages.map((page, index) => (
                  <div key={index} className="bg-white rounded-lg border shadow-sm">
                    <div className="flex items-center justify-between p-3 border-b bg-gray-50">
                      <h3 className="font-semibold text-gray-700">
                        {language === 'Hebrew' ? `עמוד ${index + 1}` : `Page ${index + 1}`}
                      </h3>
                      <div className="flex items-center gap-2">
                        {editingPageIndex !== index && regeneratingPageIndex !== index && (
                          <>
                            <button
                              onClick={() => handleRegeneratePage(index)}
                              disabled={regenerationsRemaining <= 0}
                              className={`flex items-center gap-1 text-sm font-medium transition ${
                                regenerationsRemaining <= 0
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                              title={
                                regenerationsRemaining <= 0
                                  ? "You're out of regenerations for this story!"
                                  : `Regenerate this page with ChatGPT (${regenerationsRemaining} left)`
                              }
                            >
                              🔄 Regenerate
                              {regenerationsRemaining <= 0 && (
                                <span className="text-xs">(0/{regenerationsLimit})</span>
                              )}
                            </button>
                            <button
                              onClick={() => handleEditPage(index)}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition"
                            >
                              ✏️ Edit
                            </button>
                          </>
                        )}
                        {regeneratingPageIndex === index && (
                          <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
                            🔄 Regenerating...
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-4">
                      {regeneratingPageIndex === index ? (
                        // Regenerating Mode
                        <div className="text-center py-8">
                          <LogoLoader size="sm" className="mb-3" />
                          <p className="text-green-700 font-medium">ChatGPT is rewriting this page...</p>
                          <p className="text-green-600 text-sm mt-1">This may take 10-30 seconds</p>
                        </div>
                      ) : editingPageIndex === index ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <label className="sr-only" htmlFor={`story-page-edit-${index}`}>
                            Edit page {index + 1}
                          </label>
                          <textarea
                            id={`story-page-edit-${index}`}
                            value={editingPageContent}
                            onChange={(e) => setEditingPageContent(e.target.value)}
                            className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Edit your page content..."
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSavePage}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                            >
                              💾 Save Changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed">
                          {page}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Fallback display if parsing failed
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed">
                  {generatedStory}
                </div>
              </div>
            )}
            
            {apiUsage && !usedFallback && (
              <div className="text-sm text-green-700 mb-4">
                <p>📊 ChatGPT tokens used: {apiUsage.totalTokens || 'N/A'}</p>
              </div>
            )}

            {/* Regeneration Status */}
            <div className="bg-blue-50 p-3 rounded-lg mb-4 border-l-4 border-blue-400">
              <p className="text-blue-800 text-sm">
                <strong>Regenerations:</strong> {regenerationsUsed}/{regenerationsLimit} used 
                {regenerationsRemaining > 0 ? (
                  <span className="text-blue-600"> • {regenerationsRemaining} remaining</span>
                ) : (
                  <span className="text-red-600"> • No regenerations left</span>
                )}
              </p>
            </div>
            
            {/* Download Story Button */}
            <div className="text-center mt-6">
              <button
                onClick={downloadStory}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold"
              >
                💾 Download Story
              </button>
            </div>
          </div>
        )}

        {/* Development Mode - Show Mock Story for Editing */}
        {isDevelopmentMode && generatedStory && (
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-green-800">✨ Edit Pages</h2>
              <span className="text-sm text-gray-600">
                {storyPages.length} page{storyPages.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            {/* Story Pages - Editable */}
            {storyPages.length > 0 ? (
              <div className="space-y-4 mb-4">
                {storyPages.map((page, index) => (
                  <div key={index} className="bg-white rounded-lg border shadow-sm">
                    <div className="flex items-center justify-between p-3 border-b bg-gray-50">
                      <h3 className="font-semibold text-gray-700">
                        {language === 'Hebrew' ? `עמוד ${index + 1}` : `Page ${index + 1}`}
                      </h3>
                    </div>
                    <div className="p-4">
                      {editingPageIndex === index ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <label className="sr-only" htmlFor={`mock-story-page-edit-${index}`}>
                            Edit page {index + 1}
                          </label>
                          <textarea
                            id={`mock-story-page-edit-${index}`}
                            value={editingPageContent}
                            onChange={(e) => setEditingPageContent(e.target.value)}
                            className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Edit your page content..."
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSavePage}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                            >
                              💾 Save Changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between">
                          <div className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed flex-1">
                            {page}
                          </div>
                          <button
                            onClick={() => {
                              setEditingPageIndex(index);
                              setEditingPageContent(page);
                            }}
                            className="ml-4 px-3 py-1 text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            ✏️ Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Fallback display if parsing failed
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed">
                  {generatedStory}
                </div>
              </div>
            )}
            
            {/* Download Story Button for Development Mode */}
            <div className="text-center mt-6">
              <button
                onClick={downloadStory}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold"
              >
                💾 Download Story
              </button>
            </div>
          </div>
        )}


        {/* Retry Button for Failed State */}
        {generationStatus === 'failed' && (
          <div className="text-center mb-6">
            <button
              onClick={() => handleGenerateStory()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold"
            >
              🔄 Try Again
            </button>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="absolute bottom-6 left-6 right-6 flex justify-between px-4 z-10">
        <button
          onClick={handleBack}
          disabled={isLoading || generationStatus === 'generating-story'}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-8 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
        >
          ← Back to Edit
        </button>

        {(generationStatus === 'completed' || isDevelopmentMode) && (
          <button
            onClick={async () => {
              // Ensure story is saved to database before navigating (especially important in admin/dev flow).
              const textToSave = (pastedStoryText || '').trim() || latestStoryRef.current || (generatedStory || '').trim();
              if (textToSave && sessionId) {
                await saveStoryToDatabase(textToSave);
              }
              
              if (isDevelopmentMode) {
                // Admin/dev flow: always go to character analysis next (consistent with button label).
                navigate("/create/character-analysis");
              } else {
                // In production mode: auto-analyze characters, then go to photo training
                // Character analysis will happen automatically when ImageStudio loads
                // But we need to navigate to photo-training first
                navigate("/create/photo-training");
              }
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105 flex items-center gap-2"
          >
            {isDevelopmentMode ? '🔍 Next: Character Analysis →' : '🎨 Next: Image Studio →'}
          </button>
        )}
        {generationStatus !== 'completed' && !isDevelopmentMode && (
          <button
            onClick={handleStartOver}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-xl text-lg shadow-md transition transform hover:scale-105"
          >
            🔄 Create New Story
          </button>
        )}
      </div>
      </main>

      {/* Custom Modal */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm ? handleModalConfirm : undefined}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        icon={modalState.icon}
      />
    </div>
  );
};

export default StoryStudio;